import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: (props: { id: string; "aria-invalid": boolean; "aria-describedby": string | undefined }) => ReactNode;
}

/** Rótulo, ajuda e erro ligados ao controle por id, para leitores de tela. */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="ds-field">
      <label className="ds-field__label" htmlFor={id}>{label}</label>
      {children({ id, "aria-invalid": Boolean(error), "aria-describedby": describedBy })}
      {error ? <span id={`${id}-error`} className="ds-field__error" role="alert">{error}</span> : hint ? <span id={`${id}-hint`} className="ds-field__hint">{hint}</span> : null}
    </div>
  );
}

type Common = { label: ReactNode; hint?: ReactNode; error?: string | null };

export function TextField({ label, hint, error, ...rest }: Common & InputHTMLAttributes<HTMLInputElement>) {
  return <Field label={label} hint={hint} error={error}>{(field) => <input className="ds-input" {...field} {...rest} />}</Field>;
}

export function SelectField({ label, hint, error, children, ...rest }: Common & SelectHTMLAttributes<HTMLSelectElement>) {
  return <Field label={label} hint={hint} error={error}>{(field) => <select className="ds-input" {...field} {...rest}>{children}</select>}</Field>;
}

export function TextAreaField({ label, hint, error, ...rest }: Common & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <Field label={label} hint={hint} error={error}>{(field) => <textarea className="ds-input" {...field} {...rest} />}</Field>;
}

export function Checkbox({ label, hint, ...rest }: { label: ReactNode; hint?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="ds-check">
      <input type="checkbox" {...rest} />
      <span><strong>{label}</strong>{hint ? <small>{hint}</small> : null}</span>
    </label>
  );
}

export const FormRow = ({ children }: { children: ReactNode }) => <div className="ds-form-row">{children}</div>;
